import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Save, CheckCircle, AlertTriangle, Zap, Radio, Shield, Activity } from 'lucide-react';
import { MissionScenario, SectorType } from '../types/game';
import { PrimitiveType } from '../types/progression';

export type WizardStep = 'preset' | 'task' | 'target' | 'purpose' | 'method' | 'state' | 'review';

export interface WizardState {
  // Step 0: Preset
  preset: string | null;

  // Step 1: Task
  task: string;
  
  // Step 2: Target
  targetSectors: SectorType[];
  targetAudience: string;
  
  // Step 3: Purpose
  purpose: string;
  endStateMetric: string;
  successThreshold: number;
  
  // Step 4: Method
  enabledDomains: PrimitiveType[];
  
  // Step 5: State
  initialTrust: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  scenarioName: string;
  scenarioDescription: string;
}

export const SCENARIO_PRESETS = [
  {
    id: 'cyber-range',
    name: 'Cyber Range',
    icon: Shield,
    description: 'Focus on network defense, electronic warfare, and technical exploitation.',
    domains: ['EW', 'TechnicalActivities', 'OPSEC'] as PrimitiveType[],
    task: 'Defend',
    purpose: 'Maintain 99% uptime and data integrity against state-sponsored attacks.',
    targetSectors: ['Defense', 'Finance', 'Transportation'] as SectorType[]
  },
  {
    id: 'propaganda',
    name: 'Propaganda Simulator',
    icon: Radio,
    description: 'Control the narrative through media, psychological operations, and deception.',
    domains: ['PublicAffairs', 'PsyOp', 'DeceptiveActivities'] as PrimitiveType[],
    task: 'Influence',
    purpose: 'Shape public opinion to achieve 80% favorable sentiment.',
    targetSectors: ['Leadership', 'Education', 'Public Safety'] as SectorType[]
  },
  {
    id: 'crisis',
    name: 'Humanitarian Crisis',
    icon: Activity,
    description: 'Manage information during a natural disaster or civil unrest.',
    domains: ['PublicAffairs', 'CivilMilitaryOps', 'OPSEC'] as PrimitiveType[],
    task: 'Inform',
    purpose: 'Coordinate relief efforts and minimize civilian panic.',
    targetSectors: ['Healthcare', 'Agriculture', 'Transportation'] as SectorType[]
  },
  {
    id: 'training',
    name: 'Combined Arms Training',
    icon: Zap,
    description: 'Full spectrum operations integrating all capabilities.',
    domains: ['OPSEC', 'PublicAffairs', 'Intel', 'EW', 'TechnicalActivities', 'DeceptiveActivities', 'PsyOp'] as PrimitiveType[],
    task: 'Degrade',
    purpose: 'Neutralize insurgent influence and restore legitimate governance.',
    targetSectors: ['Defense', 'Leadership', 'Public Safety'] as SectorType[]
  }
];

export const INITIAL_WIZARD_STATE: WizardState = {
  preset: null,
  task: '',
  targetSectors: [],
  targetAudience: 'Broad',
  purpose: '',
  endStateMetric: 'Trust Score',
  successThreshold: 75,
  enabledDomains: ['OPSEC', 'PublicAffairs'],
  initialTrust: 50,
  difficulty: 'Medium',
  scenarioName: '',
  scenarioDescription: ''
};

export const ScenarioBuilderWizard: React.FC<{
  onSave: (scenario: MissionScenario) => void;
  onCancel: () => void;
}> = ({ onSave, onCancel }) => {
  const [step, setStep] = useState<WizardStep>('preset');
  const [state, setState] = useState<WizardState>(INITIAL_WIZARD_STATE);
  const [error, setError] = useState<string | null>(null);

  const updateState = (updates: Partial<WizardState>) => {
    setState(prev => ({ ...prev, ...updates }));
    setError(null); // Clear error on state change
  };

  const applyPreset = (presetId: string) => {
    const preset = SCENARIO_PRESETS.find(p => p.id === presetId);
    if (preset) {
      updateState({
        preset: presetId,
        task: preset.task,
        purpose: preset.purpose,
        enabledDomains: preset.domains,
        targetSectors: preset.targetSectors,
        scenarioName: preset.name,
        scenarioDescription: preset.description
      });
    }
  };

  const steps: WizardStep[] = ['preset', 'task', 'target', 'purpose', 'method', 'state', 'review'];
  const currentStepIndex = steps.indexOf(step);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const validateStep = (currentStep: WizardStep): boolean => {
    setError(null);
    switch (currentStep) {
      case 'preset':
        if (!state.preset) {
          setError('Please select a preset to continue.');
          return false;
        }
        return true;
      case 'task':
        if (!state.task) {
          setError('Please define the primary task.');
          return false;
        }
        return true;
      case 'target':
        if (state.targetSectors.length === 0) {
          setError('Please select at least one target sector.');
          return false;
        }
        return true;
      case 'purpose':
        if (!state.purpose) {
          setError('Please define the purpose of the operation.');
          return false;
        }
        return true;
      case 'method':
        if (state.enabledDomains.length === 0) {
          setError('Please enable at least one domain.');
          return false;
        }
        return true;
      case 'state':
        if (!state.scenarioName) {
          setError('Please name your scenario.');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      if (currentStepIndex < steps.length - 1) {
        setStep(steps[currentStepIndex + 1]);
      }
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setStep(steps[currentStepIndex - 1]);
    }
  };

  const handleFinish = () => {
    const scenario: MissionScenario = {
      id: `custom-${Date.now()}`,
      name: state.scenarioName || 'Untitled Scenario',
      description: state.scenarioDescription || state.purpose,
      objectives: [state.purpose],
      turnLimit: 20,
      startingTrust: state.initialTrust,
      difficulty: state.difficulty
    };
    onSave(scenario);
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 text-slate-100 rounded-xl overflow-hidden">
      {/* Header & Progress */}
      <div className="p-6 border-b border-slate-800 bg-slate-900/50">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Scenario Builder Wizard</h2>
          <div className="text-sm text-slate-400">Step {currentStepIndex + 1} of {steps.length}</div>
        </div>
        <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-ops-blue transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {step === 'preset' && <PresetStep state={state} onSelect={applyPreset} />}
        {step === 'task' && <TaskStep state={state} updateState={updateState} />}
        {step === 'target' && <TargetStep state={state} updateState={updateState} />}
        {step === 'purpose' && <PurposeStep state={state} updateState={updateState} />}
        {step === 'method' && <MethodStep state={state} updateState={updateState} />}
        {step === 'state' && <StateStep state={state} updateState={updateState} />}
        {step === 'review' && <ReviewStep state={state} />}
      </div>

      {/* Footer & Navigation */}
      <div className="p-6 border-t border-slate-800 bg-slate-900/50 flex justify-between items-center">
        <button 
          onClick={currentStepIndex === 0 ? onCancel : handleBack}
          className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
        >
          {currentStepIndex === 0 ? 'Cancel' : 'Back'}
        </button>
        
        <div className="flex items-center gap-4">
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm animate-pulse">
              <AlertTriangle size={16} />
              {error}
            </div>
          )}
          
          {step === 'review' ? (
            <button 
              onClick={handleFinish}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg font-bold transition-colors"
            >
              <Save size={18} />
              Save Scenario
            </button>
          ) : (
            <button 
              onClick={handleNext}
              className="flex items-center gap-2 px-6 py-2 bg-ops-blue hover:bg-blue-600 text-white rounded-lg font-bold transition-colors"
            >
              Next
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PresetStep = ({ state, onSelect }: { state: WizardState, onSelect: (id: string) => void }) => (
  <div className="space-y-6">
    <h3 className="text-2xl font-bold text-white">Choose a Starting Point</h3>
    <p className="text-slate-400">Select a preset to auto-configure the scenario, or start from scratch.</p>
    <div className="grid grid-cols-2 gap-4">
      {SCENARIO_PRESETS.map(preset => {
        const Icon = preset.icon;
        return (
          <button
            key={preset.id}
            onClick={() => onSelect(preset.id)}
            className={`p-6 rounded-xl border text-left transition-all group ${
              state.preset === preset.id
                ? 'bg-ops-blue/20 border-ops-blue ring-1 ring-ops-blue'
                : 'bg-slate-800 border-slate-700 hover:border-slate-600 hover:bg-slate-750'
            }`}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className={`p-2 rounded-lg ${state.preset === preset.id ? 'bg-ops-blue text-white' : 'bg-slate-700 text-slate-300 group-hover:bg-slate-600'}`}>
                <Icon size={24} />
              </div>
              <h4 className="text-lg font-bold text-white">{preset.name}</h4>
            </div>
            <p className="text-sm text-slate-400 mb-4">{preset.description}</p>
            <div className="flex flex-wrap gap-2">
              {preset.domains.slice(0, 3).map(d => (
                <span key={d} className="text-xs px-2 py-1 bg-slate-900 rounded text-slate-300 border border-slate-700">
                  {d}
                </span>
              ))}
              {preset.domains.length > 3 && (
                <span className="text-xs px-2 py-1 bg-slate-900 rounded text-slate-300 border border-slate-700">
                  +{preset.domains.length - 3}
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

const TaskStep = ({ state, updateState }: { state: WizardState, updateState: (u: Partial<WizardState>) => void }) => (
  <div className="space-y-6">
    <h3 className="text-2xl font-bold text-white">What is your core objective?</h3>
    <div className="grid grid-cols-2 gap-4">
      {['Inform', 'Influence', 'Disrupt', 'Degrade', 'Deny', 'Discover', 'Deceive', 'Defend'].map(task => (
        <button
          key={task}
          onClick={() => updateState({ task })}
          className={`p-4 rounded-lg border text-left transition-all ${
            state.task === task 
              ? 'bg-ops-blue/20 border-ops-blue ring-1 ring-ops-blue' 
              : 'bg-slate-800 border-slate-700 hover:border-slate-600'
          }`}
        >
          <div className="font-bold text-lg mb-1">{task}</div>
          <div className="text-sm text-slate-400">Select this to focus on {task.toLowerCase()} operations.</div>
        </button>
      ))}
    </div>
  </div>
);

const TargetStep = ({ state, updateState }: { state: WizardState, updateState: (u: Partial<WizardState>) => void }) => (
  <div className="space-y-6">
    <h3 className="text-2xl font-bold text-white">Who is your target?</h3>
    <div className="grid grid-cols-3 gap-4">
      {['Defense', 'Finance', 'Healthcare', 'Transportation', 'Education', 'Public Safety', 'Manufacturing', 'Agriculture', 'Leadership'].map(sector => (
        <button
          key={sector}
          onClick={() => {
            const sectors = state.targetSectors.includes(sector as SectorType)
              ? state.targetSectors.filter(s => s !== sector)
              : [...state.targetSectors, sector as SectorType];
            updateState({ targetSectors: sectors });
          }}
          className={`p-3 rounded border text-center transition-all ${
            state.targetSectors.includes(sector as SectorType)
              ? 'bg-ops-blue/20 border-ops-blue text-white'
              : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
          }`}
        >
          {sector}
        </button>
      ))}
    </div>
  </div>
);

const PurposeStep = ({ state, updateState }: { state: WizardState, updateState: (u: Partial<WizardState>) => void }) => (
  <div className="space-y-6">
    <h3 className="text-2xl font-bold text-white">Why are you doing this?</h3>
    <div>
      <label className="block text-sm font-medium text-slate-400 mb-2">Purpose Statement</label>
      <textarea 
        value={state.purpose}
        onChange={(e) => updateState({ purpose: e.target.value })}
        className="w-full h-32 bg-slate-800 border border-slate-700 rounded-lg p-4 text-white focus:border-ops-blue focus:outline-none"
        placeholder="e.g., Reduce public trust in financial institutions to destabilize the economy..."
      />
    </div>
  </div>
);

const MethodStep = ({ state, updateState }: { state: WizardState, updateState: (u: Partial<WizardState>) => void }) => (
  <div className="space-y-6">
    <h3 className="text-2xl font-bold text-white">How will you accomplish this?</h3>
    <div className="space-y-3">
      {['OPSEC', 'PublicAffairs', 'Intel', 'EW', 'TechnicalActivities', 'DeceptiveActivities', 'PsyOp'].map(domain => (
        <label key={domain} className="flex items-center p-4 bg-slate-800 rounded-lg border border-slate-700 cursor-pointer hover:bg-slate-750">
          <input 
            type="checkbox"
            checked={state.enabledDomains.includes(domain as PrimitiveType)}
            onChange={(e) => {
              const domains = e.target.checked
                ? [...state.enabledDomains, domain as PrimitiveType]
                : state.enabledDomains.filter(d => d !== domain);
              updateState({ enabledDomains: domains });
            }}
            className="w-5 h-5 rounded border-slate-600 text-ops-blue focus:ring-ops-blue bg-slate-900"
          />
          <span className="ml-3 font-medium text-white">{domain}</span>
        </label>
      ))}
    </div>
  </div>
);

const StateStep = ({ state, updateState }: { state: WizardState, updateState: (u: Partial<WizardState>) => void }) => (
  <div className="space-y-6">
    <h3 className="text-2xl font-bold text-white">Implementation Details</h3>
    <div className="grid grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-slate-400 mb-2">Scenario Name</label>
        <input 
          type="text"
          value={state.scenarioName}
          onChange={(e) => updateState({ scenarioName: e.target.value })}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white focus:border-ops-blue focus:outline-none"
          placeholder="My Custom Scenario"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-400 mb-2">Difficulty</label>
        <select 
          value={state.difficulty}
          onChange={(e) => updateState({ difficulty: e.target.value as any })}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-white focus:border-ops-blue focus:outline-none"
        >
          <option value="Easy">Easy</option>
          <option value="Medium">Medium</option>
          <option value="Hard">Hard</option>
        </select>
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-400 mb-2">Description</label>
      <textarea 
        value={state.scenarioDescription}
        onChange={(e) => updateState({ scenarioDescription: e.target.value })}
        className="w-full h-24 bg-slate-800 border border-slate-700 rounded-lg p-3 text-white focus:border-ops-blue focus:outline-none"
        placeholder="Brief briefing for the player..."
      />
    </div>
  </div>
);

const ReviewStep = ({ state }: { state: WizardState }) => (
  <div className="space-y-6">
    <h3 className="text-2xl font-bold text-white">Review Scenario</h3>
    
    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h4 className="text-xl font-bold text-ops-blue">{state.scenarioName || 'Untitled Scenario'}</h4>
          <p className="text-slate-400">{state.scenarioDescription || 'No description provided.'}</p>
        </div>
        <div className="px-3 py-1 bg-slate-700 rounded text-sm font-medium text-slate-300">
          {state.difficulty}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-700">
        <div>
          <div className="text-sm text-slate-500 uppercase tracking-wider mb-1">Task</div>
          <div className="font-medium text-white">{state.task || 'Not specified'}</div>
        </div>
        <div>
          <div className="text-sm text-slate-500 uppercase tracking-wider mb-1">Purpose</div>
          <div className="font-medium text-white">{state.purpose || 'Not specified'}</div>
        </div>
        <div>
          <div className="text-sm text-slate-500 uppercase tracking-wider mb-1">Target Sectors</div>
          <div className="flex flex-wrap gap-2">
            {state.targetSectors.length > 0 ? (
              state.targetSectors.map(s => (
                <span key={s} className="px-2 py-1 bg-slate-900 rounded text-xs text-slate-300 border border-slate-700">
                  {s}
                </span>
              ))
            ) : (
              <span className="text-slate-500 italic">None selected</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-sm text-slate-500 uppercase tracking-wider mb-1">Enabled Capabilities</div>
          <div className="flex flex-wrap gap-2">
            {state.enabledDomains.map(d => (
              <span key={d} className="px-2 py-1 bg-slate-900 rounded text-xs text-ops-blue border border-slate-700">
                {d}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
    
    <div className="flex items-center gap-3 p-4 bg-green-900/20 border border-green-800/50 rounded-lg text-green-400">
      <CheckCircle size={20} />
      <span>Ready to generate mission profile.</span>
    </div>
  </div>
);
